import { z } from 'zod/v4';

/**
 * Mirrors the backend's `FormulaScriptError` and the `scripting_core`
 * error types it wraps. Every one of those enums uses serde's adjacently tagged representation, so
 * a variant is `{ <tag>: 'Variant' }` when it carries no payload and `{ <tag>: 'Variant', details }`
 * when it does. The tag is `type` everywhere except `Token`, which uses `token`.
 */

const ResultTypeMismatchRuntype = z.object({ type: z.literal('ResultTypeMismatch'), details: z.string() });
const UnableToConvertInputToFloatRuntype = z.object({
    type: z.literal('UnableToConvertInputToFloat'),
    details: z.object({ name: z.string() }),
});
const IncompleteRuntype = z.object({ type: z.literal('Incomplete') });
const InternalServerErrorRuntype = z.object({ type: z.literal('InternalServerError'), details: z.string() });

const LexerErrorKindRuntype = z.union([
    z.object({ type: z.literal('InvalidCharacter'), details: z.string() }),
    /** The script is longer than the formula evaluator accepts; `details` is the maximum length in characters. */
    z.object({ type: z.literal('ScriptTooLong'), details: z.number() }),
]);

export type ErrorSpan = z.infer<typeof SpanRuntype>;
const SpanRuntype = z.object({
    start_line: z.number(),
    start_column: z.number(),
    end_line: z.number(),
    end_column: z.number(),
});

export type LexerErrorDTO = z.infer<typeof LexerErrorRuntype>;
const LexerErrorRuntype = z.object({
    type: z.literal('LexerError'),
    details: z.object({ kind: LexerErrorKindRuntype, span: SpanRuntype }),
});

/** The `Token` variants that carry no payload, i.e. the ones that stand for a fixed symbol. */
export type TokenType = z.infer<typeof TokenWithoutDetailsRuntypes>;
const TokenWithoutDetailsRuntypes = z.union([
    z.literal('If'),
    z.literal('ElseIf'),
    z.literal('Else'),
    z.literal('Plus'),
    z.literal('Minus'),
    z.literal('Divide'),
    z.literal('Multiply'),
    z.literal('And'),
    z.literal('Or'),
    z.literal('Equal'),
    z.literal('NotEqual'),
    z.literal('GreaterThanOrEqual'),
    z.literal('LessThanOrEqual'),
    z.literal('GreaterThan'),
    z.literal('LessThan'),
    z.literal('Comma'),
    z.literal('LParen'),
    z.literal('RParen'),
    z.literal('LBrace'),
    z.literal('RBrace'),
    z.literal('Assignment'),
    z.literal('Semicolon'),
    z.literal('Eof'),
    z.literal('Blank'),
]);

const TokenRuntypeWithDetailsOfStringType = z.object({
    token: z.union([z.literal('Ident'), z.literal('String'), z.literal('SingleLineComment')]),
    details: z.string(),
});
const TokenRuntypeWithDetailsOfBooleanType = z.object({
    token: z.literal('Boolean'),
    details: z.boolean(),
});

const TokenRuntypeWithDetailsOfNumberType = z.object({
    token: z.literal('Float'),
    details: z.number(),
});

const TokenRuntypeWithoutDetails = z.object({
    token: TokenWithoutDetailsRuntypes,
});

export type SyntaxErrorTokenKind = z.infer<typeof TokenRuntype>;
const TokenRuntype = z.union([
    TokenRuntypeWithDetailsOfBooleanType,
    TokenRuntypeWithDetailsOfStringType,
    TokenRuntypeWithDetailsOfNumberType,
    TokenRuntypeWithoutDetails,
]);

/**
 * `SyntaxErrorDataType`: either a concrete token, or one of the token classes the parser expects
 * without caring about the exact token (an identifier, a string, a number, a boolean).
 */
export type SyntaxErrorDetails = z.infer<typeof SyntaxErrorDataTypeRuntype>;
const SyntaxErrorDataTypeRuntype = z.union([
    z.object({ type: z.literal('Token'), details: TokenRuntype }),
    z.object({ type: z.literal('Ident') }),
    z.object({ type: z.literal('String') }),
    z.object({ type: z.literal('Float') }),
    z.object({ type: z.literal('Boolean') }),
]);

const ExpectedRuntype = z.object({
    type: z.literal('Expected'),
    details: SyntaxErrorDataTypeRuntype,
});

const ExpectedAnyOfRuntype = z.object({
    type: z.literal('ExpectedAnyOf'),
    details: z.array(SyntaxErrorDataTypeRuntype),
});

const UnexpectedRuntype = z.object({
    type: z.literal('Unexpected'),
    details: SyntaxErrorDataTypeRuntype,
});

/** The formula nests deeper than the parser accepts; `details` is the maximum depth. */
const NestingTooDeepRuntype = z.object({
    type: z.literal('NestingTooDeep'),
    details: z.number(),
});

export type SyntaxErrorKind = z.infer<typeof SyntaxErrorKindRuntype>;
const SyntaxErrorKindRuntype = z.union([
    ExpectedRuntype,
    ExpectedAnyOfRuntype,
    UnexpectedRuntype,
    NestingTooDeepRuntype,
]);

export type SyntaxErrorDTO = z.infer<typeof SyntaxErrorRuntype>;
export const SyntaxErrorRuntype = z.object({
    type: z.literal('SyntaxError'),
    details: z.object({
        kind: SyntaxErrorKindRuntype,
        span: SpanRuntype,
    }),
});

export type UndefinedIdentifierKind = z.infer<typeof UndefinedIdentifierRuntype>;
const UndefinedIdentifierRuntype = z.object({
    type: z.literal('UndefinedIdentifier'),
    details: z.string(),
});
export type ReservedKeywordKind = z.infer<typeof ReservedKeywordRuntype>;

const ReservedKeywordRuntype = z.object({
    type: z.literal('ReservedKeyword'),
    details: z.string(),
});

export type RuntimeErrorKind = z.infer<typeof RuntimeErrorKindRuntype>;
const RuntimeErrorKindRuntype = z.union([
    z.object({
        type: z.union([
            z.literal('ExpectedBoolean'),
            z.literal('ExpectedFloat'),
            z.literal('ExpectedCall'),
            z.literal('ArgumentMissing'),
            z.literal('DivideByZero'),
            z.literal('TooManyArguments'),
            z.literal('ExpectedUnsignedInteger'),
            z.literal('OutOfI32Range'),
            z.literal('NestingTooDeep'),
        ]),
    }),
    UndefinedIdentifierRuntype,
    ReservedKeywordRuntype,
]);

export type RuntimeErrorDTO = z.infer<typeof RuntimeErrorRuntype>;
const RuntimeErrorRuntype = z.object({
    kind: RuntimeErrorKindRuntype,
    span: SpanRuntype,
});

export type ValidationErrorDTO = z.infer<typeof ValidationErrorRuntype>;
const ValidationErrorRuntype = z.object({
    type: z.literal('ValidationError'),
    details: RuntimeErrorKindRuntype,
});

export type RuntimeTestErrorDTO = z.infer<typeof RuntimeTestErrorRuntype>;
const RuntimeTestErrorRuntype = z.object({
    type: z.literal('RuntimeError'),
    details: RuntimeErrorRuntype,
});

export type FormulaErrorDTO = z.infer<typeof FormulaScriptErrorRuntype>;
export const FormulaScriptErrorRuntype = z.union([
    ResultTypeMismatchRuntype,
    UnableToConvertInputToFloatRuntype,
    IncompleteRuntype,
    InternalServerErrorRuntype,
    LexerErrorRuntype,
    SyntaxErrorRuntype,
    ValidationErrorRuntype,
    RuntimeTestErrorRuntype,
]);
